import type { EmailOtpType } from '@supabase/supabase-js';
import { getSupabase } from '@/lib/supabase';
import type { Role } from '@/types/domain';
import type { AuthOutcome } from './telegram';

/* LINE Login (OAuth 2.0). Mirrors the Telegram path: the verify Edge Function
   (`line-auth`) exchanges the code for the LINE profile, redeems the invite for
   new users, and mints a magic-link token the client swaps for a session. */

const CHANNEL_ID = (import.meta.env.VITE_LINE_CHANNEL_ID as string) ?? '2010431092';
const APP_URL = (import.meta.env.VITE_APP_URL as string) ?? 'https://warmpaper.club';
const REDIRECT_URI = `${APP_URL}/auth/line/callback`;
const FN_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/line-auth`;
const ANON = import.meta.env.VITE_SUPABASE_ANON_KEY as string;

// The CSRF nonce AND the invite ride inside the OAuth `state` param (which
// round-trips through LINE), not just localStorage. LINE's published app-to-app
// login can reopen the callback in the DEFAULT browser (Safari) — a different
// context than where login started — wiping localStorage. Relying on storage
// alone made every cross-context signup fail ("Couldn't sign you in") with no
// account created. localStorage still holds the nonce for a strict same-context
// CSRF check when it survives.
const SS_STATE = 'wp:line:state';

function encodeState(nonce: string, invite?: string | null, checkin?: string | null): string {
  return btoa(JSON.stringify({ n: nonce, i: invite ?? null, c: checkin ?? null }));
}
function decodeState(state: string | null): { n?: string; i?: string | null; c?: string | null } {
  if (!state) return {};
  try {
    return JSON.parse(atob(state)) as { n?: string; i?: string | null; c?: string | null };
  } catch {
    return {};
  }
}

/** Kick off LINE login: stash a CSRF nonce + carry it (and the invite) in `state`. */
export function startLineLogin(inviteToken?: string | null, checkin?: string | null) {
  const nonce = crypto.randomUUID();
  localStorage.setItem(SS_STATE, nonce);

  const url = new URL('https://access.line.me/oauth2/v2.1/authorize');
  url.searchParams.set('response_type', 'code');
  url.searchParams.set('client_id', CHANNEL_ID);
  url.searchParams.set('redirect_uri', REDIRECT_URI);
  url.searchParams.set('state', encodeState(nonce, inviteToken, checkin));
  url.searchParams.set('scope', 'profile openid');
  window.location.href = url.toString();
}

/** The invite travels in `state`, so it survives even when storage is wiped. */
export function readLineInvite(state: string | null): string | null {
  return decodeState(state).i ?? null;
}

/** A pending QR check-in carried through login as "course|token", or null. */
export function readLineCheckin(state: string | null): { course: string; token: string } | null {
  const c = decodeState(state).c;
  if (!c) return null;
  const [course, token] = c.split('|');
  return course && token ? { course, token } : null;
}

/** Accept any well-formed state we issued (must decode to a nonce). We
 *  deliberately do NOT require the localStorage nonce to match: LINE's
 *  app-to-app login returns the callback in a different browser context (no
 *  storage) or with a stale nonce from a prior attempt, both of which would
 *  wrongly block real users. Login is invite-gated and the OAuth code is
 *  single-use + bound to our redirect_uri + channel secret, so login-CSRF
 *  risk is low. */
export function validateLineState(state: string | null): boolean {
  return !!decodeState(state).n;
}

/** Clear the stashed CSRF nonce once the callback has consumed it. */
export function clearLineLogin() {
  localStorage.removeItem(SS_STATE);
}

/** Send the LINE code to the verify Edge Function, then establish the session. */
export async function postLineAuth(code: string, inviteToken?: string | null): Promise<AuthOutcome> {
  let res: Response;
  try {
    res = await fetch(FN_URL, {
      method: 'POST',
      headers: { 'content-type': 'application/json', apikey: ANON, Authorization: `Bearer ${ANON}` },
      body: JSON.stringify({ code, redirect_uri: REDIRECT_URI, invite_token: inviteToken ?? null }),
    });
  } catch (e) {
    console.error('[line-auth] network/CORS error — function unreachable', e);
    return { ok: false, reason: 'error' };
  }

  const body = (await res.json().catch(() => ({}))) as Record<string, unknown>;
  if (!res.ok) {
    if (body.error === 'no_account_needs_invite') return { ok: false, reason: 'needs-invite' };
    if (body.error === 'account_suspended') return { ok: false, reason: 'suspended' };
    console.error('[line-auth] function returned', res.status, body);
    return { ok: false, reason: 'error' };
  }

  const { error } = await getSupabase().auth.verifyOtp({
    token_hash: String(body.token_hash),
    type: String(body.type || 'magiclink') as EmailOtpType,
  });
  if (error) {
    console.error('[line-auth] verifyOtp failed', error);
    return { ok: false, reason: 'error' };
  }
  return { ok: true, onboarded: body.onboarded === true, role: body.role as Role };
}
